import React, { useState, useEffect } from "react";
import "./Dashboard.css";
import { fetchDashboardData, deleteReport } from '../components/api';
import { useNavigate } from "react-router-dom";
import useTranslation from '../hooks/useTranslation';
import { buildDailyRows } from '../utils/dashboardUtils';
import { EmptyState } from './DashboardWidgets';
import WeeklyStatistics from './WeeklyStatistics';
import PeriodStatistics from './PeriodStatistics';
import XiTopNav from './xi/XiTopNav';
import XiBottomNav from './xi/XiBottomNav';
import XiBackdrop from './xi/XiBackdrop';
import useSessionUser from '../hooks/useSessionUser';

function getToday() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

export default function Dashboard() {
  const [dailyRows, setDailyRows] = useState([]);

  const [aggRes, setAggRes] = useState(null); // backend aggregate RES for non-daily

  const [prevAggRes, setPrevAggRes] = useState(null); // previous period aggregate for trends
  const [weeklyRecords, setWeeklyRecords] = useState([]);
  const [weeklyRange, setWeeklyRange] = useState({ start: null, end: null });
  const [periodRecords, setPeriodRecords] = useState([]);
  const [periodRange, setPeriodRange] = useState({ start: null, end: null });
  const [todaysOverall, setTodaysOverall] = useState(null); // today's overall average if multiple reports
  const [activeTab, setActiveTab] = useState("Daily");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout, isAuthenticated } = useSessionUser();

  const withChrome = (content) => (
    <div className="xi-page-shell">
      <div className="xi-app-frame">
        <XiBackdrop variant="page" />
        <XiTopNav
          user={user}
          isAuthenticated={isAuthenticated}
          onRequireLogin={() => navigate('/login')}
          onLogout={logout}
          onDeactivate={() => navigate('/delete')}
          onOpenSubscription={() => navigate('/plans')}
        />
        <main className="xi-page xi-dashboard">{content}</main>
        <XiBottomNav />
      </div>
    </div>
  );

  // removed temporary clean duplicates handler



  // Delete report handler

  const handleDeleteReport = async (reportDate) => {

    if (!window.confirm(t('Are you sure you want to delete this report? This action cannot be undone.'))) {

      return;

    }



    try {

      const userId = localStorage.getItem("neuro_user_id");

      if (!userId) {

        alert(t('User not logged in!'));

        return;

      }



      const res = await deleteReport(userId, reportDate);

      if (res && res.status_code === 200) {

        // Refresh the dashboard data

        const type = activeTab.toLowerCase();

        const payload = { user_id: userId, current_date: getToday(), type };

        const response = await fetchDashboardData(payload);

        const data = response.data?.data || {};

        const records = (data.records || []).filter(r => r && (r.report_date || r.created_at));



        // Set today's overall data if available

        setTodaysOverall(data.todays_overall || null);



        if (activeTab === 'Daily') {
          const rows = buildDailyRows(records);
          setDailyRows(rows);
        } else {

          setAggRes(data);

        }



        alert(t('Report deleted successfully!'));

      } else {

        alert(res?.message || t('Failed to delete report'));

      }

    } catch (error) {

      console.error('Delete report error:', error);

      alert(t('Failed to delete report'));

    }

  };



  useEffect(() => {
    async function fetchDashboard() {
      const user_id = localStorage.getItem("neuro_user_id");
      if (!user_id) {
        setError(t('User not logged in!'));
        setDailyRows([]);

        setAggRes(null);

        setPrevAggRes(null);
        return;
      }
      setLoading(true);
      setError(null);

      try {

        const type = activeTab.toLowerCase();

        const payload = { user_id, current_date: getToday(), type };

        const res = await fetchDashboardData(payload);
        const data = res.data?.data || {};

        const records = (data.records || []).filter(r => r && (r.report_date || r.created_at));

        // Set today's overall data if available

        setTodaysOverall(data.todays_overall || null);

        if (activeTab === 'Daily') {
          const daily = buildDailyRows(records);
          setDailyRows(daily);
          setAggRes(null);
          setPrevAggRes(null);
          setWeeklyRecords([]);
          setWeeklyRange({ start: null, end: null });
          setPeriodRecords([]);
          setPeriodRange({ start: null, end: null });
        } else {

          // Use backend-provided aggregate for non-daily views

          setAggRes(data.aggregate?.RES || null);
          if (activeTab === 'Weekly') {
            setWeeklyRecords(records);
            setWeeklyRange({ start: data.start_date, end: data.end_date });
          } else {
            setWeeklyRecords([]);
            setWeeklyRange({ start: null, end: null });
          }
          if (activeTab === 'Monthly' || activeTab === 'Yearly') {
            setPeriodRecords(records);
            setPeriodRange({ start: data.start_date, end: data.end_date });
          } else {
            setPeriodRecords([]);
            setPeriodRange({ start: null, end: null });
          }

          setDailyRows([]);



          // Fetch previous period aggregate for comparison

          const prevDate = (() => {

            const d = new Date();

            if (type === 'weekly') d.setDate(d.getDate() - 7);

            else if (type === 'monthly') d.setDate(d.getDate() - 30);

            else d.setDate(d.getDate() - 365);

            return d.toISOString().slice(0, 10);

          })();

          try {

            const prev = await fetchDashboardData({ user_id, current_date: prevDate, type });

            setPrevAggRes(prev.data?.data?.aggregate?.RES || null);

          } catch (_e) {

            setPrevAggRes(null);

          }

        }

      } catch (e) {

        setError(t('Failed to load dashboard. ') + (e?.response?.data?.message || e.message));

        setDailyRows([]);

        setAggRes(null);
        setPrevAggRes(null);
        setWeeklyRecords([]);
        setWeeklyRange({ start: null, end: null });
        setPeriodRecords([]);
        setPeriodRange({ start: null, end: null });
      }
      setLoading(false);
    }
    fetchDashboard();
    // eslint-disable-next-line
  }, [activeTab]);

  function handleTabClick(tab) {
    setActiveTab(tab);
  }

  // Error UI
  if (error) {
    return withChrome(
      <div className="dashboard-center-box">
        <div className="dashboard-card">
          <div className="dash-state dash-state--error">
            <span className="dash-state__icon" role="img" aria-label="Error">🚫</span>
            <p className="dash-state__title">{error}</p>
            <div className="dash-state__text">
              Please <a href="/login">{t('log in')}</a> to view your dashboard.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return withChrome(
      <div className="dashboard-center-box">
        <div className="dashboard-card">
          <div className="dash-state dash-state--loading">
            <span className="dash-state__icon" role="img" aria-label="Loader">⏳</span>
            <p className="dash-state__title">{t('Loading your mental health dashboard...')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Daily table

  function renderDaily() {

    if (!dailyRows.length) {

      return (
        <EmptyState message={t('No daily reports yet. Record or upload to see entries here!')} />
      );

    }

    return (

      <div style={{ marginTop: 20 }}>

        {/* Today's Overall Section */}

        {todaysOverall && (

          <div className="dash-highlight">

            <h3 className="dash-highlight__title">

              📊 {t("Today's Overall")} ({todaysOverall.report_count} {t('reports')})

            </h3>

            <div className="dash-highlight__body">

              {todaysOverall.response?.RES?.top_5 || ''}

            </div>

          </div>

        )}

        <div className="dash-table-wrap">

          <table className="dash-table">

            <thead>

              <tr>

                <th style={{ textAlign: 'left' }}>{t('Date')}</th>

                <th style={{ textAlign: 'center' }}>{t('Time')}</th>

                <th style={{ textAlign: 'right' }}>{t('Happy')}</th>

                <th style={{ textAlign: 'right' }}>{t('Sad')}</th>

                <th style={{ textAlign: 'right' }}>{t('Angry')}</th>

                <th style={{ textAlign: 'right' }}>{t('Fear')}</th>

                <th style={{ textAlign: 'right' }}>{t('Disgust')}</th>

                <th style={{ textAlign: 'right' }}>{t('Surprise')}</th>

                <th style={{ textAlign: 'center', width: '80px' }}>{t('Actions')}</th>

              </tr>

            </thead>

            <tbody>

              {dailyRows.map(r => (

                <tr key={r.id || r.date}>



                  <td>{r.date}</td>



                  <td className="dash-table__time">

                    {r.time}

                  </td>



                  <td className="dash-table__num">{r.happy.toFixed(2)}%</td>

                  <td className="dash-table__num">{r.sad.toFixed(2)}%</td>

                  <td className="dash-table__num">{r.angry.toFixed(2)}%</td>

                  <td className="dash-table__num">{r.fear.toFixed(2)}%</td>

                  <td className="dash-table__num">{r.disgust.toFixed(2)}%</td>

                  <td className="dash-table__num">{r.surprise.toFixed(2)}%</td>

                  <td className="dash-table__actions">

                    <button

                      className="dash-delete-btn"

                      onClick={() => handleDeleteReport(r.id || r.date)}

                      title={t('Delete this report')}

                      aria-label={t('Delete this report')}

                    >

                      🗑️

                    </button>

                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      </div>

    );

  }

  // Weekly render logic
  function renderWeekly() {
    if (!aggRes && !weeklyRecords.length) {
      return <EmptyState message={t('No weekly data yet. Please upload more voice samples to view your weekly report!')} />;
    }
    return (
      <WeeklyStatistics
        aggregate={aggRes}
        previousAggregate={prevAggRes}
        records={weeklyRecords}
        startDate={weeklyRange.start}
        endDate={weeklyRange.end}
      />
    );
  }



  // Monthly render logic
  function renderMonthly() {
    if (!aggRes && !periodRecords.length) {
      return <EmptyState message={t('No monthly data yet. Come back after a few weeks!')} />;
    }
    return <PeriodStatistics period="monthly" aggregate={aggRes} previousAggregate={prevAggRes} records={periodRecords} startDate={periodRange.start} endDate={periodRange.end} />;
  }

  // Yearly render logic
  function renderYearly() {
    if (!aggRes && !periodRecords.length) {
      return <EmptyState message={t('No yearly data yet. Stick with it for a year to see your journey!')} />;
    }
    return (
      <PeriodStatistics
        period="yearly"
        aggregate={aggRes}
        previousAggregate={prevAggRes}
        records={periodRecords}
        startDate={periodRange.start}
        endDate={periodRange.end}
      />
    );
  }

  return withChrome(
    <div className="xi-dash">
      <div className="xi-page-head">
        <div className="xi-page-head__main">
          <span className="xi-eyebrow">{t('Your space')}</span>
          <h1 className="xi-page-head__title">{t('Wellbeing dashboard')}</h1>
          <p className="xi-page-head__sub">
            {t('Daily, weekly, monthly and yearly emotional patterns from your sessions.')}
          </p>
        </div>
        <div className="xi-page-head__actions">
          <div className="xi-tabs" role="tablist" aria-label={t('Time range')}>
            {['Daily', 'Weekly', 'Monthly', 'Yearly'].map((tab) => (
              <button
                key={tab}
                type="button"
                role="tab"
                id={`dash-tab-${tab.toLowerCase()}`}
                aria-selected={activeTab === tab}
                aria-controls="dash-panel"
                className={`xi-tab${activeTab === tab ? ' is-active' : ''}`}
                onClick={() => handleTabClick(tab)}
              >
                {t(tab)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div
        className="dashboard-card"
        id="dash-panel"
        role="tabpanel"
        aria-labelledby={`dash-tab-${activeTab.toLowerCase()}`}
      >
        {activeTab === "Daily" && renderDaily()}
        {activeTab === "Weekly" && renderWeekly()}
        {activeTab === "Monthly" && renderMonthly()}
        {activeTab === "Yearly" && renderYearly()}
      </div>
    </div>
  );
}
